import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, MenuItem, Stack, Typography } from '@mui/material';
import React from 'react';
import { useForm } from 'react-hook-form';
import FormProvider from 'common/components/form-provider';
import BaseTextField from 'common/components/base-text-field';
import { colors, styleInput, styleSelect } from 'common/constants';
import avatarDefault from 'assets/images/icons/avatar-default.svg';
import { createStyleSheet, handleError } from 'utils/helpers';
import RequiredLabel from 'common/components/required-label';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BaseImageUpload from 'common/components/base-img-upload';
import { common } from '@mui/material/colors';
import {
    getAccountInfo,
    createAccount,
    updateAccount,
} from 'services/accountManagement.service';
import { toast } from 'react-toastify';
import { ReactComponent as SyncIcon } from 'assets/images/icons/sync.svg';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import { BaseSelectField } from 'common/components/base-select-field';
import BaseSwitch from 'common/components/base-switch';
import { accountSchema } from 'common/schema';
import { setIsLoading } from 'slices/loadingSlice';

interface IAccount {
    edit?: boolean;
}
const AccountForm: React.FC<IAccount> = ({ edit }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const methods = useForm({
        resolver: yupResolver(accountSchema),
        defaultValues: {
            avatar: '',
            username: '',
            email: '',
            uid: '',
            role: 'super',
            status: true,
        },
    });
    const accountId = useParams<{ id: string }>().id;
    const {
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = methods;
    const dispatch = useDispatch();
    const onSubmit = handleSubmit(async (data) => {
        try {
            if (!edit) {
                dispatch(setIsLoading(true));
                const response = await createAccount(data);
                if (response.success) {
                    toast.success(response.message);
                    navigate('/admin/account-management');
                    dispatch(setIsLoading(false));
                } else {
                    throw new Error(response.message);
                }
            } else {
                if (!accountId) return;
                const response = await updateAccount(Number(accountId), data);
                if (response.success) {
                    toast.success(response.message);
                    navigate('/admin/account-management');
                    dispatch(setIsLoading(false));
                } else {
                    throw new Error(response.message);
                }
            }
        } catch (error) {
            dispatch(setIsLoading(false));
            handleError(error);
        }
    });

    React.useEffect(() => {
        const getUserDetail = async () => {
            try {
                if (!accountId) return;
                const response = await getAccountInfo(Number(accountId));
                if (response.success) {
                    const { attributes } = response.data.admin_management || {};
                    reset({
                        avatar: attributes.avatar,
                        username: attributes.username,
                        email: attributes.email,
                        uid: attributes.uid,
                        role: attributes.role,
                        status: attributes.status === 'active' ? true : false,
                    });
                }
            } catch (error) {
                handleError(error);
            }
        };

        if (edit && accountId) {
            getUserDetail();
        }
    }, [reset, edit, dispatch, accountId]);
    return (
        <Box
            sx={{
                marginTop: '2rem',
            }}
        >
            <Typography variant="h4" sx={styles.title}>
                {!edit ? 'Thêm mới tài khoản' : 'Chỉnh sửa tài khoản'}
            </Typography>
            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Box sx={{ marginBottom: '24px', fontWeight: '600' }}>
                    {t('common.avatar')}
                </Box>
                <Box display={'flex'} gap={'60px'}>
                    <Box>
                        <BaseImageUpload
                            name="avatar"
                            multiple={false}
                            sx={{
                                ...styles.avatar,
                                backgroundImage: watch().avatar
                                    ? `url(${watch().avatar})`
                                    : `url(${avatarDefault})`,
                                border: watch().avatar
                                    ? 'none'
                                    : `2px dotted ${colors.main}`,
                                borderRadius: '50%',
                            }}
                        />

                        {errors.avatar && (
                            <Box
                                sx={{
                                    fontSize: '14px',
                                    marginLeft: 0,
                                    color: `${colors.textError} !important`,
                                    fontWeight: '400',
                                }}
                            >
                                {errors.avatar.message}
                            </Box>
                        )}
                    </Box>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '37px',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                gap: '37px',
                            }}
                        >
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label={t('common.user.id')}
                                    required={false}
                                />
                                <BaseTextField
                                    autoComplete="off"
                                    name="uid"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    placeholder="ID-*****"
                                    disabled
                                />
                            </Stack>
                            <Stack spacing={'7px'}>
                                <RequiredLabel
                                    label={t('common.user.name')}
                                    required={false}
                                />
                                <BaseTextField
                                    autoComplete="off"
                                    name="username"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    inputProps={{ maxLength: 55 }}
                                />
                            </Stack>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                gap: '37px',
                            }}
                        >
                            <Stack spacing={'7px'} flex={1}>
                                <RequiredLabel
                                    label="Loại tài khoản"
                                    required={false}
                                />
                                <BaseSelectField
                                    name="role"
                                    sx={{
                                        ...styleSelect,
                                        '& .MuiSelect-icon': {
                                            right: '20px',
                                            fontSize: '50px ',
                                        },
                                    }}
                                    fullWidth={true}
                                >
                                    {[
                                        {
                                            value: 'super',
                                            label: 'Quản trị hệ thống',
                                        },
                                        { value: 'sales', label: 'Kinh doanh' },
                                        {
                                            value: 'accountant',
                                            label: 'Kế toán',
                                        },
                                    ].map((item, key) => (
                                        <MenuItem key={key} value={item.value}>
                                            {item.label}
                                        </MenuItem>
                                    ))}
                                </BaseSelectField>
                            </Stack>
                            <Stack spacing={'7px'}>
                                <RequiredLabel label="Email" required={false} />
                                <BaseTextField
                                    autoComplete="off"
                                    name="email"
                                    fullWidth
                                    size="small"
                                    sx={styles.input}
                                    {...(edit && { disabled: true })}
                                />
                            </Stack>
                        </Box>
                        <Stack spacing={2}>
                            <Typography
                                sx={{
                                    fontSize: '20px',
                                    lineHeight: '24.2px',
                                }}
                            >
                                Trạng thái
                            </Typography>
                            <BaseSwitch
                                name="status"
                                sx={{
                                    transform: 'scale(1.5)',
                                    '& .MuiSwitch-thumb': {
                                        backgroundColor:
                                            'var(--color-primary-light)',
                                    },
                                    '& .MuiSwitch-track': {
                                        backgroundColor: '#0000001A',
                                        opacity: 1,
                                    },
                                    '& .Mui-checked': {
                                        '& .MuiSwitch-thumb': {
                                            backgroundColor:
                                                'var(--color-primary-light)',
                                        },
                                        '& + .MuiSwitch-track': {
                                            backgroundColor: 'primary.main',
                                            opacity: '1 !important',
                                        },
                                    },
                                }}
                            />
                        </Stack>
                        <Box>
                            <Stack
                                gap={2}
                                flexDirection={'row'}
                                alignItems={'center'}
                            >
                                {!edit ? (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        sx={styles.btnEdit}
                                    >
                                        Lưu
                                    </Button>
                                ) : (
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        sx={styles.btnEdit}
                                        startIcon={<SyncIcon />}
                                    >
                                        {t('common.update')}
                                    </Button>
                                )}
                                <Button
                                    type="submit"
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => {
                                        navigate('/admin/account-management');
                                    }}
                                    startIcon={<CloseIcon />}
                                    sx={{
                                        ...styles.btnEdit,
                                        width: '155px',
                                        backgroundColor: common.white,
                                        border: `2px solid ${colors.main}`,
                                        ':hover': {
                                            backgroundColor: common.white,
                                            border: `2px solid ${colors.main}`,
                                        },
                                    }}
                                >
                                    {t('common.close')}
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </Box>
            </FormProvider>
        </Box>
    );
};

const styles = createStyleSheet({
    input: {
        '& .MuiInputBase-root': {
            ...styleInput['& .MuiInputBase-root'],
            width: '400px',
            height: '60px',
            fontSize: '22px',
        },
    },
    title: {
        fontSize: '40px',
        fontWeight: 700,
        color: colors.main,
        marginBottom: '27px',
    },
    avatar: {
        width: '348px',
        height: '348px',
        backgroundImage: `url(${avatarDefault})`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'bottom',
        borderRadius: '17px',
        border: `2px dotted ${colors.main}`,
    },
    btnEdit: {
        minWidth: '155px',
        height: '60px',
        fontSize: '20px',
        lineHeight: '24px',
        fontWeight: '700',
        borderRadius: '40px',
        textTransform: 'none',
    },
});

export default AccountForm;
